import { GenericLazyString } from '@eventbrite/i18n';
import interpolate from '@eventbrite/interpolate-lazy-string';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { TicketStatus } from '../../../models/tickets';
import { TICKET_STATUS_MAP } from '../constants';

export enum TicketCardStatusTextSize {
    large = 'large',
    medium = 'medium',
    small = 'small',
}

export interface TicketCardStatusProps {
    customClassNames?: Parameters<typeof classNames>[0];
    textSize?: TicketCardStatusTextSize;
    /**
     * This is a necessary flag for cases where the tickets may have a status causing elements to appear
     * or disappear but the status itself should not be displayed. An example of this is event-wide
     * sales not started or sales ended. Each ticket should not display it individually, but no select
     * should appear.
     **/
    shouldHideStatus: boolean;
    /**
     * A key tied to the ticketStatus map located in ticketStatusConstants.js. If nothing is passed in, then
     * no status will be displayed. Otherwise will render the associated status and also hide the
     * appropriate components as well.
     **/
    statusKey: TicketStatus | null;
    /**
     * The end datetime for ticket sales to be shown. Only required when certain statuses need
     * to be rendered.
     **/
    salesEnd: GenericLazyString | string | null;
    /**
     * The start datetime for ticket sales. Only required when rendering a status that needs the
     * date.
     **/
    salesStart: GenericLazyString | string | null;
    /**
     * If this ticket is tiered. It should not show salesStart date when status is not yet
     * started.
     **/
    isTicketTiered: boolean;
}

export const TicketCardStatus: FunctionComponent<TicketCardStatusProps> = ({
    customClassNames,
    shouldHideStatus,
    statusKey,
    salesEnd,
    salesStart,
    textSize = TicketCardStatusTextSize.medium,
}) => {
    if (shouldHideStatus || !statusKey) {
        return null;
    }

    const statusInfo = TICKET_STATUS_MAP[statusKey];
    const ticketStatusClassNames = classNames(
        'ticket-status',
        'eds-text-color--ui-600',
        {
            'eds-text-bl': textSize === TicketCardStatusTextSize.large,
            'eds-text-bm': textSize === TicketCardStatusTextSize.medium,
            'eds-text-bs': textSize === TicketCardStatusTextSize.small,
            'ticket-status--no-wrap': [
                TicketStatus.salesEnded,
                TicketStatus.soldOut,
                TicketStatus.unavailable,
            ].includes(statusKey),
        },
        customClassNames,
    );

    const date = statusInfo.datePropKey
        ? statusInfo.datePropKey === 'salesEnd'
            ? salesEnd
            : salesStart
        : '';

    return (
        <span
            className={ticketStatusClassNames}
            data-spec="eds-ticket-card-content-status"
            data-testid="ticket-card-status"
        >
            {interpolate(statusInfo.displayText.toString(), { date }, true)}
        </span>
    );
};

export default TicketCardStatus;
